import React, { useState, useEffect, useMemo } from 'react';
import {
  StyleSheet, Text, View, TextInput, TouchableOpacity, ScrollView, ActivityIndicator,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import Data, { authHeader } from './Data';
import { useScale, ScaleFn } from './common';

const C = {
  bg: '#F9FAFB', card: '#FFFFFF', cardInner: '#F3F4F6',
  border: '#E5E7EB', text: '#111827', textSub: '#4B5563',
  muted: '#9CA3AF', accent: '#38BDF8', ok: '#34D399', warn: '#FB923C', err: '#EF4444',
};

// 「기저귀 기록 조회」의 환자 목록(diaper/personList)에 안 뜨는 사람들.
// personList 가 `IS_CURRENT=1 JOIN SENSORS` 로 거르기 때문에 두 종류가 여기 걸린다:
//   센서 삭제 — 배정은 살아있는데 그 센서가 SENSORS 에서 지워진 경우
//   배정 해제 — 활성 배정이 아예 없는 경우(센서는 멀쩡할 수 있다)
// 현재 쓰는 센서가 있는 사람은 여기 없다 — 그쪽은 일반 화면에서 받으면
// personRawCsv 가 배정 이력 전체를 훑어 삭제 센서 구간까지 같이 나온다.
type Row = {
  IDX: number; NAME: string; STATUS: string;
  DEL_SENSORS: number; SENSORS: string;
  PERIOD_FROM: string; PERIOD_TO: string; RECS: number;
};

const fmtDate = (d: Date) => d.toISOString().slice(0, 10);
const today = new Date();
const twoMonthAgo = new Date(); twoMonthAgo.setMonth(twoMonthAgo.getMonth() - 2);

export default function DeletedSensorRecords() {
  const isFocused = useIsFocused();
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  // 원시데이터 보존이 약 2개월이라 기본값을 그 범위로 잡는다.
  const [startDate, setStartDate] = useState<string>(fmtDate(twoMonthAgo));
  const [endDate, setEndDate] = useState<string>(fmtDate(today));
  const [busy, setBusy] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setMsg(null);
    Data.fetchData('diaper/deletedSensorPersonList', {
      startDate: startDate + ' 00:00:00', endDate: endDate + ' 23:59:59',
    }, (result: Row[]) => {
      setRows(result || []);
      setLoading(false);
    }, () => setLoading(false));
  };

  useEffect(() => { if (isFocused) load(); }, [isFocused]);

  const q = query.trim().toLowerCase();
  const view = rows.filter((r) =>
    !q || [r.NAME, r.SENSORS].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));

  const download = async (r: Row) => {
    setBusy(r.IDX);
    setMsg(null);
    try {
      const resp = await fetch('sensor/personRawCsv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
        body: JSON.stringify({
          PERSON_IDX: r.IDX,
          startDate: startDate + ' 00:00:00',
          endDate: endDate + ' 23:59:59',
        }),
      });
      if (!resp.ok) { setMsg((await resp.text()) || '다운로드 실패'); return; }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${r.NAME}_raw_${startDate}_${endDate}.csv`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMsg('다운로드 중 오류');
    } finally {
      setBusy(null);
    }
  };

  return (
    <ScrollView style={styles.page} contentContainerStyle={{ padding: s(16) }}>
      <Text style={styles.title}>배정 없는 이용자 기록</Text>
      <Text style={styles.desc}>
        센서가 삭제되었거나 배정이 해제되어 「기저귀 기록 조회」에 나오지 않는 이용자입니다.
        기록은 남아 있어 CSV 로 받을 수 있습니다.
      </Text>

      <View style={styles.dateRow}>
        <View style={{ flex: 1 }}>
          <Text style={styles.label}>시작</Text>
          <TextInput value={startDate} onChangeText={setStartDate} style={styles.input} placeholder="YYYY-MM-DD" />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.label}>종료</Text>
          <TextInput value={endDate} onChangeText={setEndDate} style={styles.input} placeholder="YYYY-MM-DD" />
        </View>
        <View style={{ flex: 1.4 }}>
          <Text style={styles.label}>검색</Text>
          <TextInput value={query} onChangeText={setQuery} style={styles.input} placeholder="이름 / 센서ID" />
        </View>
        <TouchableOpacity onPress={load} style={styles.loadBtn}>
          <Text style={styles.loadBtnText}>새로고침</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.hint}>
        ※ 원시 데이터는 약 2개월치만 보관됩니다. 그 이전 기간은 빈 파일이 나올 수 있습니다.
      </Text>

      {msg && <Text style={styles.err}>{msg}</Text>}

      {loading ? (
        <View style={styles.center}><ActivityIndicator color={C.accent} /></View>
      ) : view.length === 0 ? (
        <View style={styles.center}>
          <Text style={styles.empty}>
            {rows.length === 0 ? '삭제된 센서 기록이 있는 이용자가 없습니다.' : '검색 결과가 없습니다.'}
          </Text>
        </View>
      ) : (
        <View style={styles.table}>
          <View style={[styles.tr, styles.thead]}>
            <Text style={[styles.th, { flex: 1.2 }]}>이름</Text>
            <Text style={[styles.th, { flex: 1 }]}>상태</Text>
            <Text style={[styles.th, { flex: 2.2 }]}>센서 ID</Text>
            <Text style={[styles.th, { flex: 1.8 }]}>배정 기간</Text>
            <Text style={[styles.th, { flex: 0.8 }]}>기록</Text>
            <Text style={[styles.th, { flex: 1 }]}> </Text>
          </View>
          {view.map((r) => (
            <View key={r.IDX} style={styles.tr}>
              <Text style={[styles.td, styles.name, { flex: 1.2 }]}>{r.NAME}</Text>
              <View style={{ flex: 1, paddingHorizontal: s(10) }}>
                <Text style={[styles.badge, r.STATUS === '센서 삭제' ? styles.badgeDel : styles.badgeOff]}>
                  {r.STATUS}
                </Text>
              </View>
              <Text style={[styles.td, styles.mono, { flex: 2.2 }]} numberOfLines={2}>{r.SENSORS}</Text>
              <Text style={[styles.td, { flex: 1.8 }]}>{r.PERIOD_FROM} ~ {r.PERIOD_TO}</Text>
              <Text style={[styles.td, { flex: 0.8 }]}>{r.RECS}건</Text>
              <View style={{ flex: 1, alignItems: 'flex-end', paddingRight: s(10) }}>
                <TouchableOpacity
                  onPress={() => download(r)}
                  disabled={busy === r.IDX}
                  style={[styles.csvBtn, busy === r.IDX && { opacity: 0.5 }]}
                >
                  <Text style={styles.csvBtnText}>{busy === r.IDX ? '받는 중...' : 'CSV'}</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </View>
      )}
    </ScrollView>
  );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
  page: { flex: 1, backgroundColor: C.bg },
  title: { fontSize: s(20), fontWeight: '700', color: C.text, marginBottom: s(6) },
  desc: { fontSize: s(13), color: C.textSub, marginBottom: s(14), lineHeight: s(20) },
  dateRow: { flexDirection: 'row', alignItems: 'flex-end', gap: s(10), marginBottom: s(10) },
  label: { fontSize: s(12), color: C.textSub, marginBottom: s(4) },
  input: {
    backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: s(8),
    paddingHorizontal: s(10), paddingVertical: s(8), fontSize: s(13), color: C.text,
  },
  loadBtn: {
    backgroundColor: C.accent, borderRadius: s(8),
    paddingHorizontal: s(16), paddingVertical: s(9),
  },
  loadBtnText: { color: '#FFFFFF', fontWeight: '700', fontSize: s(13) },
  hint: { fontSize: s(12), color: C.muted, marginBottom: s(12) },
  err: { fontSize: s(13), color: C.err, marginBottom: s(10) },
  center: { paddingVertical: s(40), alignItems: 'center' },
  empty: { fontSize: s(14), color: C.muted },
  table: { backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: s(10), overflow: 'hidden' },
  tr: { flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderBottomColor: C.border, minHeight: s(46) },
  thead: { backgroundColor: C.cardInner },
  th: { fontSize: s(12), fontWeight: '700', color: C.textSub, paddingHorizontal: s(10) },
  td: { fontSize: s(13), color: C.text, paddingHorizontal: s(10), paddingVertical: s(8) },
  name: { fontWeight: '700' },
  mono: { fontSize: s(12), color: C.textSub },
  badge: {
    fontSize: s(11), fontWeight: '700', textAlign: 'center',
    paddingVertical: s(3), paddingHorizontal: s(6), borderRadius: s(4), overflow: 'hidden',
  },
  badgeDel: { backgroundColor: '#FEE2E2', color: '#B91C1C' },
  badgeOff: { backgroundColor: '#FEF3C7', color: '#B45309' },
  csvBtn: {
    backgroundColor: C.accent, borderRadius: s(6),
    paddingHorizontal: s(12), paddingVertical: s(6),
  },
  csvBtnText: { color: '#FFFFFF', fontWeight: '700', fontSize: s(12) },
});
